import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, ArrowUpDown, ArrowUp, ArrowDown, Users, MoreHorizontal } from 'lucide-react';

// ──────────────────────────────────────────────────────────────────────────────
// Contact-type schema registry
// ──────────────────────────────────────────────────────────────────────────────
const REPAIR_SITE_FIELDS = [
  { key: 'repair_site_name', label: 'Repair Site Name', placeholder: 'e.g. Chicago Yard' },
  { key: 'splc_code', label: 'SPLC Code', placeholder: 'SPLC code' },
  { key: 'address', label: 'Address', placeholder: 'Street address' },
  // 2026-08-19 — Address Line 2 is optional for Repair Site (users were
  // typing "N/A" to satisfy the required check, which then leaked into
  // the Work Order Entry Requirements modal).
  { key: 'address_line_2', label: 'Address Line 2', placeholder: 'Suite, unit, etc.', optional: true },
  { key: 'city', label: 'City' },
  { key: 'state_province', label: 'State/Province' },
  { key: 'zip_code', label: 'ZIP Code' },
  { key: 'site_contact', label: 'Site Contact', placeholder: 'Contact name' },
  { key: 'site_contact_number', label: 'Site Contact Number', placeholder: 'Phone number' },
  { key: 'site_contact_email', label: 'Site Contact Email', type: 'email', placeholder: 'email@example.com' },
];

const SERVICE_REQUESTOR_FIELDS = [
  { key: 'requestor_full_name', label: 'Full Name' },
  { key: 'requestor_company', label: 'Company' },
  { key: 'requestor_phone', label: 'Phone' },
  { key: 'requestor_email', label: 'Email', type: 'email', placeholder: 'email@example.com' },
];

const BILLING_PARTY_FIELDS = [
  { key: 'billing_company', label: 'Company' },
  { key: 'billing_contact_name', label: 'Primary Contact Name' },
  { key: 'billing_contact_email', label: 'Primary Contact Email', type: 'email', placeholder: 'email@example.com' },
  { key: 'billing_contact_phone', label: 'Phone', placeholder: 'Phone number' },
];

// 2026-07-05 Wave 2: Dedicated Site now uses the same field set as Repair Site
// (Site Name, SPLC, Address, City/State/ZIP, Site Contact + phone + email,
// Entry Requirements). Only the site name label differs.
const DEDICATED_SITE_FIELDS = [
  { key: 'repair_site_name', label: 'Dedicated Site Name', placeholder: 'e.g. Home Shop' },
  { key: 'splc_code', label: 'SPLC Code', placeholder: 'SPLC code' },
  { key: 'address', label: 'Address', placeholder: 'Street address' },
  { key: 'address_line_2', label: 'Address Line 2', placeholder: 'Suite, unit, etc.' },
  { key: 'city', label: 'City' },
  { key: 'state_province', label: 'State/Province' },
  { key: 'zip_code', label: 'ZIP Code' },
  { key: 'site_contact', label: 'Site Contact', placeholder: 'Contact name' },
  { key: 'site_contact_number', label: 'Site Contact Phone Number', placeholder: 'Phone number' },
  { key: 'site_contact_email', label: 'Site Contact Email', type: 'email', placeholder: 'email@example.com' },
];

const CONTACT_TYPES = [
  { value: 'REPAIR_SITE', label: 'Repair Site', fields: REPAIR_SITE_FIELDS },
  { value: 'SERVICE_REQUESTOR', label: 'Service Requestor', fields: SERVICE_REQUESTOR_FIELDS },
  { value: 'RESPONSIBLE_BILLING_PARTY', label: 'Responsible Billing Party', fields: BILLING_PARTY_FIELDS },
  { value: 'DEDICATED_SITE', label: 'Dedicated Site', fields: DEDICATED_SITE_FIELDS },
];

const CONTACT_TYPE_LABEL = Object.fromEntries(CONTACT_TYPES.map(t => [t.value, t.label]));
const FIELDS_BY_TYPE = Object.fromEntries(CONTACT_TYPES.map(t => [t.value, t.fields]));

const ALL_FIELD_KEYS = [
  ...REPAIR_SITE_FIELDS, ...SERVICE_REQUESTOR_FIELDS, ...BILLING_PARTY_FIELDS,
].map(f => f.key).concat(['entry_requirements']);

const buildEmptyForm = (type = 'REPAIR_SITE') => ({
  contact_type: type,
  facility_id: '',
  ...Object.fromEntries(ALL_FIELD_KEYS.map(k => [k, ''])),
});

// Map any contact record to the shared 6-column grid row.
function toGridRow(contact, facilityMap) {
  const type = contact.contact_type || 'REPAIR_SITE';
  let company_site = '';
  let name = '';
  let email = '';
  let phone = '';
  if (type === 'REPAIR_SITE' || type === 'DEDICATED_SITE') {
    company_site = contact.repair_site_name || '';
    name = contact.site_contact || '';
    email = contact.site_contact_email || '';
    phone = contact.site_contact_number || '';
  } else if (type === 'SERVICE_REQUESTOR') {
    company_site = contact.requestor_company || '';
    name = contact.requestor_full_name || '';
    email = contact.requestor_email || '';
    phone = contact.requestor_phone || '';
  } else if (type === 'RESPONSIBLE_BILLING_PARTY') {
    company_site = contact.billing_company || '';
    name = contact.billing_contact_name || '';
    email = contact.billing_contact_email || '';
    phone = contact.billing_contact_phone || '';
  }
  const facility_name = contact.facility_id
    ? (facilityMap?.[contact.facility_id] || '—')
    : 'Legacy';
  return {
    id: contact.id,
    contact_type: type,
    company_site,
    facility_id: contact.facility_id || '',
    facility_name,
    name,
    email,
    phone,
    _raw: contact,
  };
}

// ──────────────────────────────────────────────────────────────────────────────
function SortHeader({ field, sortBy, sortDir, onSort, children, className = '' }) {
  const active = sortBy === field;
  const Icon = !active ? ArrowUpDown : sortDir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <TableHead className={className}>
      <button
        type="button"
        onClick={() => onSort(field)}
        className="inline-flex items-center gap-1 font-medium hover:text-[#005EF8]"
        data-testid={`sort-${field}`}
      >
        {children}
        <Icon className="w-3.5 h-3.5" />
      </button>
    </TableHead>
  );
}

function CustomersContactsPage() {
  const { api, hasRole, user, currentFacility } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [sortBy, setSortBy] = useState('company_site');
  const [sortDir, setSortDir] = useState('asc');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [form, setForm] = useState(buildEmptyForm('REPAIR_SITE'));
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const canManage = hasRole(['ADMIN', 'MANAGER', 'OPERATOR']);
  const canDelete = hasRole(['ADMIN', 'MANAGER']);
  // Phase 3: Operators / Technicians only ever own contacts under a single
  // facility, so the facility picker is effectively locked for them.
  const facilityFieldLocked = hasRole(['OPERATOR', 'TECHNICIAN']) && facilities.length <= 1;

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      // Phase 3 follow-up: also filter by the top-left facility selector
      // (currentFacility). "all" means "use my role scope" — the backend
      // decides Admin=org-wide vs Manager=assigned-set.
      const params = {};
      if (currentFacility && currentFacility !== 'all') {
        params.facility_id = currentFacility;
      }
      const res = await api.get('/repair-sites', { params });
      setContacts(res.data.map(c => ({ contact_type: 'REPAIR_SITE', ...c })));
    } catch { toast.error('Failed to load contacts'); }
    finally { setLoading(false); }
  }, [api, currentFacility]);

  // Fetch the caller's in-scope facilities so the Add/Edit form can only
  // offer facilities the user actually owns. `/facilities` is already
  // role-scoped by Phase 1.
  const fetchFacilities = useCallback(async () => {
    try {
      const res = await api.get('/facilities');
      setFacilities(res.data || []);
    } catch { /* non-fatal — form validation will still block bad submits */ }
  }, [api]);

  useEffect(() => { fetchContacts(); fetchFacilities(); }, [fetchContacts, fetchFacilities]);

  // Preselect a sensible default owning facility when opening the create
  // dialog: the currently active facility if it's in scope, otherwise
  // the first in-scope facility. Falls back to blank for admin org-wide.
  const defaultFacilityId = () => {
    if (currentFacility && currentFacility !== 'all' && facilities.some(f => f.id === currentFacility)) {
      return currentFacility;
    }
    if (facilities.length === 1) return facilities[0].id;
    return '';
  };

  const openCreate = () => {
    setEditingContact(null);
    setForm({ ...buildEmptyForm('REPAIR_SITE'), facility_id: defaultFacilityId() });
    setDialogOpen(true);
  };

  const openEdit = (contact) => {
    setEditingContact(contact);
    setForm({
      ...buildEmptyForm(contact.contact_type || 'REPAIR_SITE'),
      ...Object.fromEntries(ALL_FIELD_KEYS.map(k => [k, contact[k] || ''])),
      contact_type: contact.contact_type || 'REPAIR_SITE',
      facility_id: contact.facility_id || '',
    });
    setDialogOpen(true);
  };

  const handleTypeChange = (value) => {
    // Reset only the fields when switching types; preserve facility_id.
    setForm(prev => ({
      ...buildEmptyForm(value),
      facility_id: prev.facility_id || '',
    }));
  };

  const handleSave = async () => {
    if (!form.facility_id) {
      toast.error('Facility is required');
      return;
    }
    const activeFields = FIELDS_BY_TYPE[form.contact_type] || [];
    for (const f of activeFields) {
      if (f.optional) continue;  // 2026-08-19 — respect optional fields.
      if (!form[f.key]?.trim()) {
        toast.error(`${f.label} is required`);
        return;
      }
    }
    setSaving(true);
    try {
      // Send facility_id + contact_type + this type's fields to keep records clean.
      const payload = {
        facility_id: form.facility_id,
        contact_type: form.contact_type,
      };
      for (const f of activeFields) payload[f.key] = form[f.key];
      // Entry Requirements is optional and shared by REPAIR_SITE + DEDICATED_SITE.
      if (form.contact_type === 'REPAIR_SITE' || form.contact_type === 'DEDICATED_SITE') {
        payload.entry_requirements = form.entry_requirements || '';
      }
      if (editingContact) {
        await api.put(`/repair-sites/${editingContact.id}`, payload);
        toast.success('Contact updated');
      } else {
        await api.post('/repair-sites', payload);
        toast.success('Contact created');
      }
      setDialogOpen(false);
      fetchContacts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const requestDelete = (gridRow) => {
    setDeleteTarget(gridRow);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/repair-sites/${deleteTarget.id}`);
      toast.success('Contact deleted');
      setDeleteTarget(null);
      fetchContacts();
    } catch { toast.error('Failed to delete'); }
    finally { setDeleting(false); }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
  };

  const visibleContacts = useMemo(() => {
    const q = search.trim().toLowerCase();
    const facilityMap = Object.fromEntries(facilities.map(f => [f.id, f.name]));
    const rows = contacts.map(c => toGridRow(c, facilityMap));
    let list = rows.filter(r => {
      if (typeFilter !== 'ALL' && r.contact_type !== typeFilter) return false;
      if (!q) return true;
      // Search across shared display fields plus city/SPLC from underlying record.
      const raw = r._raw || {};
      const haystack = [
        r.company_site, r.name, r.email, r.phone, r.facility_name,
        raw.city, raw.splc_code, raw.state_province,
      ].join(' ').toLowerCase();
      return haystack.includes(q);
    });
    list.sort((a, b) => {
      const av = (a[sortBy] || '').toString().toLowerCase();
      const bv = (b[sortBy] || '').toString().toLowerCase();
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [contacts, facilities, search, typeFilter, sortBy, sortDir]);

  const updateField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#005EF8]" />
      </div>
    );
  }

  const activeFields = FIELDS_BY_TYPE[form.contact_type] || [];

  return (
    <div className="space-y-6" data-testid="customers-contacts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#323033]">Customers & Contacts</h1>
          <p className="text-sm text-[#5A5A5A]">Directory of customers, repair sites, and operational contacts.</p>
        </div>
        {canManage && (
          <Button onClick={openCreate} data-testid="add-contact-btn">
            <Plus className="w-4 h-4 mr-2" /> Add New Contact
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#808080]" />
          <Input
            className="pl-9"
            placeholder="Search by name, company, city, or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="search-contacts"
          />
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-sm text-[#5A5A5A] whitespace-nowrap">Contact Type</Label>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-56" data-testid="filter-contact-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All types</SelectItem>
              {CONTACT_TYPES.map(t => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {visibleContacts.length === 0 ? (
            <div className="py-16 text-center">
              <Users className="w-10 h-10 mx-auto text-[#808080] mb-3" />
              <p className="text-[#5A5A5A]">
                {contacts.length === 0 ? 'No contacts saved yet' : 'No results match your filters'}
              </p>
              {contacts.length === 0 && canManage && (
                <Button variant="outline" className="mt-4" onClick={openCreate}>
                  <Plus className="w-4 h-4 mr-1" /> Add your first contact
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="contacts-table">
                <TableHeader>
                  <TableRow>
                    <SortHeader field="company_site" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>
                      Company / Site Name
                    </SortHeader>
                    <SortHeader field="facility_name" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>
                      Facility
                    </SortHeader>
                    <SortHeader field="contact_type" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>
                      Contact Type
                    </SortHeader>
                    <SortHeader field="name" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>
                      Primary Contact Name
                    </SortHeader>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    {(canManage || canDelete) && <TableHead className="text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleContacts.map(r => (
                    <TableRow key={r.id} data-testid={`contact-row-${r.id}`}>
                      <TableCell className="font-medium text-[#323033]">{r.company_site}</TableCell>
                      <TableCell
                        className="text-[#5A5A5A] whitespace-nowrap"
                        data-testid={`contact-facility-${r.id}`}
                      >
                        {r.facility_name}
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[#005EF8]/10 text-[#005EF8]">
                          {CONTACT_TYPE_LABEL[r.contact_type] || 'Repair Site'}
                        </span>
                      </TableCell>
                      <TableCell>{r.name}</TableCell>
                      <TableCell className="text-[#5A5A5A]">{r.email}</TableCell>
                      <TableCell className="whitespace-nowrap">{r.phone}</TableCell>
                      {(canManage || canDelete) && (
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                data-testid={`actions-trigger-${r.id}`}
                                aria-label="Open actions menu"
                              >
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-32">
                              {canManage && (
                                <DropdownMenuItem
                                  onClick={() => openEdit(r._raw)}
                                  data-testid={`edit-contact-${r.id}`}
                                >
                                  <Pencil className="w-3.5 h-3.5 mr-2" /> Edit
                                </DropdownMenuItem>
                              )}
                              {canDelete && (
                                <DropdownMenuItem
                                  onClick={() => requestDelete(r)}
                                  className="text-red-600 focus:text-red-600 focus:bg-red-50"
                                  data-testid={`delete-contact-${r.id}`}
                                >
                                  <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingContact ? 'Edit Contact' : 'Add New Contact'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            {/* Phase 3: Owning facility is REQUIRED and is the very first
                field. Options are limited to the caller's in-scope
                facilities (already role-filtered by /facilities). */}
            <div>
              <Label>Facility *</Label>
              <Select
                value={form.facility_id || ''}
                onValueChange={(v) => setForm(prev => ({ ...prev, facility_id: v }))}
                disabled={facilityFieldLocked}
              >
                <SelectTrigger data-testid="form-facility-id">
                  <SelectValue placeholder="Select owning facility" />
                </SelectTrigger>
                <SelectContent>
                  {facilities.map(f => (
                    <SelectItem
                      key={f.id}
                      value={f.id}
                      data-testid={`form-facility-option-${f.id}`}
                    >
                      {f.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {facilityFieldLocked && (
                <p className="text-xs text-[#808080] mt-1">Facility is locked to your assigned facility.</p>
              )}
              {!facilityFieldLocked && hasRole(['MANAGER']) && (
                <p className="text-xs text-[#808080] mt-1">
                  Pick which of your assigned facilities owns this contact.
                </p>
              )}
            </div>

            <div>
              <Label>Contact Type *</Label>
              <Select
                value={form.contact_type}
                onValueChange={handleTypeChange}
                disabled={!!editingContact}
              >
                <SelectTrigger data-testid="form-contact-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONTACT_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {editingContact && (
                <p className="text-xs text-[#808080] mt-1">Contact type cannot be changed after creation.</p>
              )}
            </div>

            <div className="space-y-3">
              {(form.contact_type === 'REPAIR_SITE' || form.contact_type === 'DEDICATED_SITE') ? (
                <RepairSiteFields
                  form={form}
                  updateField={updateField}
                  siteNameLabel={form.contact_type === 'DEDICATED_SITE' ? 'Dedicated Site Name' : 'Repair Site Name'}
                  fieldDefs={activeFields}
                />
              ) : (
                activeFields.map(f => (
                  <div key={f.key}>
                    <Label>{f.label} *</Label>
                    <Input
                      type={f.type || 'text'}
                      value={form[f.key] || ''}
                      onChange={(e) => updateField(f.key, e.target.value)}
                      placeholder={f.placeholder || ''}
                      data-testid={`form-${f.key.replace(/_/g, '-')}`}
                    />
                  </div>
                ))
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-contact-btn">
                {saving ? 'Saving...' : editingContact ? 'Update Contact' : 'Save Contact'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open && !deleting) setDeleteTarget(null); }}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete contact?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete{' '}
              <span className="font-medium text-[#323033]">
                {deleteTarget?.company_site || 'this saved contact'}
              </span>
              {deleteTarget?.name ? <> ({deleteTarget.name})</> : null}
              ? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              data-testid="delete-confirm-btn"
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Keep the original Repair Site form layout (3-col city/state/zip) to preserve UX.
// 2026-07-05 Wave 2: also reused by DEDICATED_SITE (same field set, different label).
function RepairSiteFields({ form, updateField, siteNameLabel, fieldDefs }) {
  const defs = fieldDefs || REPAIR_SITE_FIELDS;
  return (
    <>
      {['repair_site_name', 'splc_code', 'address', 'address_line_2'].map((k) => {
        const f = defs.find(x => x.key === k) || REPAIR_SITE_FIELDS.find(x => x.key === k);
        const label = k === 'repair_site_name' ? (siteNameLabel || f.label) : f.label;
        const isOptional = !!f.optional;
        return (
          <div key={k}>
            <Label>{label}{isOptional ? '' : ' *'}</Label>
            <Input
              value={form[k] || ''}
              onChange={(e) => updateField(k, e.target.value)}
              placeholder={f.placeholder || ''}
              data-testid={`form-${k.replace(/_/g, '-')}`}
            />
          </div>
        );
      })}
      <div className="grid grid-cols-3 gap-3">
        {['city', 'state_province', 'zip_code'].map((k) => {
          const f = REPAIR_SITE_FIELDS.find(x => x.key === k);
          return (
            <div key={k}>
              <Label>{f.label} *</Label>
              <Input
                value={form[k] || ''}
                onChange={(e) => updateField(k, e.target.value)}
                data-testid={`form-${k.replace(/_/g, '-')}`}
              />
            </div>
          );
        })}
      </div>
      <hr className="my-2" />
      {['site_contact', 'site_contact_number', 'site_contact_email'].map((k) => {
        const f = REPAIR_SITE_FIELDS.find(x => x.key === k);
        return (
          <div key={k}>
            <Label>{f.label} *</Label>
            <Input
              type={f.type || 'text'}
              value={form[k] || ''}
              onChange={(e) => updateField(k, e.target.value)}
              placeholder={f.placeholder || ''}
              data-testid={`form-${k.replace(/_/g, '-')}`}
            />
          </div>
        );
      })}
      {/* Entry Requirements — optional, free-text. Surfaced on the WS detail
          page through a read-only "Entry Requirements" modal so techs in the
          field can see gate codes, badging, PPE notes, etc. without leaving
          the work order. */}
      <div>
        <Label>Entry Requirements</Label>
        <Textarea
          value={form.entry_requirements || ''}
          onChange={(e) => updateField('entry_requirements', e.target.value)}
          maxLength={3000}
          rows={4}
          data-testid="form-entry-requirements"
        />
        <p className="text-[11px] text-[#5A5A5A] mt-1">
          Optional. Max 3,000 characters. {((form.entry_requirements || '').length)}/3000
        </p>
      </div>
    </>
  );
}

export default CustomersContactsPage;
